import { EnterpriseInquiryForm } from "@/components/billing/EnterpriseInquiryForm";
import { ContactSuccessIcon } from "@/components/ContactSuccessIcon";
import { config } from "@/config";
import { ROUTES } from "@/constants/routes";
import { Button } from "@cohesive/ui-components";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

export function OnboardingEnterpriseInquiryPage() {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const navigate = useNavigate();

  const handleSuccess = () => {
    setIsSubmitted(true);
  };

  const handleClose = () => {
    navigate(ROUTES.ONBOARDING.SELECT_PLAN);
  };

  return (
    <>
      <title>Enterprise Plan Inquiry</title>

      {config.recaptcha.siteKey && (
        <script
          async
          src={`https://www.google.com/recaptcha/enterprise.js?render=${config.recaptcha.siteKey}`}
        ></script>
      )}

      {isSubmitted ? (
        <div className="flex items-center justify-center min-h-full py-10 px-4">
          <div className="max-w-2xl mx-auto">
            <div className="relative z-10 text-center space-y-8">
              {/* Success icon with animation */}
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", stiffness: 260, damping: 20 }}
                className="flex items-center justify-center mx-auto"
              >
                <ContactSuccessIcon className="w-20 h-20 text-lc-active dark:text-lc-active" />
              </motion.div>

              {/* Main heading */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.2 }}
                className="space-y-4"
              >
                <h1 className="text-3xl md:text-4xl font-semibold text-foreground leading-tight">
                  Inquiry Received
                </h1>
                <p className="text-xl font-light text-foreground/80 max-w-2xl mx-auto">
                  Thank you for your interest in our Enterprise plan. We're
                  excited to learn more about your organization's needs!
                </p>
              </motion.div>

              {/* What happens next */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.4 }}
                className="bg-muted/30 rounded-lg p-6 lg:p-8 space-y-6"
              >
                <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                  What happens next?
                </h2>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="space-y-3">
                    <div className="w-12 h-12 bg-foreground/10 rounded-full flex items-center justify-center mx-auto">
                      <span className="text-foreground font-bold text-lg">
                        1
                      </span>
                    </div>
                    <div className="space-y-2">
                      <h3 className="font-semibold text-foreground">Review</h3>
                      <p className="text-foreground/80 font-light text-sm">
                        Our Enterprise team will review your requirements
                      </p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="w-12 h-12 bg-foreground/10 rounded-full flex items-center justify-center mx-auto">
                      <span className="text-foreground font-bold text-lg">
                        2
                      </span>
                    </div>
                    <div className="space-y-2">
                      <h3 className="font-semibold text-foreground">Contact</h3>
                      <p className="text-foreground/80 font-light text-sm">
                        We'll reach out to schedule a personalized demo
                      </p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="w-12 h-12 bg-foreground/10 rounded-full flex items-center justify-center mx-auto">
                      <span className="text-foreground font-bold text-lg">
                        3
                      </span>
                    </div>
                    <div className="space-y-2">
                      <h3 className="font-semibold text-foreground">Propose</h3>
                      <p className="text-foreground/80 font-light text-sm">
                        Get a custom Enterprise plan tailored to your needs
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Continue with a standard plan */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3, delay: 0.6 }}
                className="pt-4 space-y-4"
              >
                <p className="text-muted-foreground">
                  Want to get started right away? You can start with a standard
                  plan and upgrade later.
                </p>
                <Button
                  onClick={() => navigate(ROUTES.ONBOARDING.SELECT_PLAN)}
                  size="lg"
                >
                  Choose a plan to get started
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </motion.div>
            </div>
          </div>
        </div>
      ) : (
        <div className="container mx-auto max-w-7xl py-8 px-6">
          <EnterpriseInquiryForm
            onSuccess={handleSuccess}
            onClose={handleClose}
          />
        </div>
      )}
    </>
  );
}
